export type AtlasEdgeLike = {
  edge_id: string;
  source: string;
  target: string;
  score: number;
};

export type SimilarityPath = {
  nodeIds: string[];
  edgeIds: string[];
  totalCost: number;
};

type Neighbor = {
  nodeId: string;
  edgeId: string;
  score: number;
};

export function neighborsFor(
  edges: AtlasEdgeLike[],
  paperId: string | null,
): Neighbor[] {
  if (!paperId) return [];
  return edges
    .filter((edge) => edge.source === paperId || edge.target === paperId)
    .map((edge) => ({
      nodeId: edge.source === paperId ? edge.target : edge.source,
      edgeId: edge.edge_id,
      score: edge.score,
    }))
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.nodeId.localeCompare(right.nodeId);
    });
}

export function selectedNeighborhood(
  edges: AtlasEdgeLike[],
  paperId: string | null,
): Set<string> {
  const result = new Set<string>();
  if (!paperId) return result;
  result.add(paperId);
  for (const row of neighborsFor(edges, paperId)) {
    result.add(row.nodeId);
  }
  return result;
}

export function shortestSimilarityPath(
  edges: AtlasEdgeLike[],
  source: string | null,
  target: string | null,
): SimilarityPath | null {
  if (!source || !target) return null;
  if (source === target) {
    return { nodeIds: [source], edgeIds: [], totalCost: 0 };
  }

  const adjacency = new Map<
    string,
    Array<{ nodeId: string; edgeId: string; cost: number }>
  >();

  const add = (
    from: string,
    to: string,
    edgeId: string,
    score: number,
  ) => {
    const list = adjacency.get(from) ?? [];
    list.push({
      nodeId: to,
      edgeId,
      cost: -Math.log(Math.max(score, 1e-6)),
    });
    adjacency.set(from, list);
  };

  for (const edge of edges) {
    add(edge.source, edge.target, edge.edge_id, edge.score);
    add(edge.target, edge.source, edge.edge_id, edge.score);
  }

  for (const list of adjacency.values()) {
    list.sort((left, right) => {
      if (left.cost !== right.cost) return left.cost - right.cost;
      return left.nodeId.localeCompare(right.nodeId);
    });
  }

  const distances = new Map<string, number>([[source, 0]]);
  const signatures = new Map<string, string>([[source, source]]);
  const previous = new Map<string, { nodeId: string; edgeId: string }>();
  const queue = new Set<string>(adjacency.keys());
  queue.add(source);
  queue.add(target);

  while (queue.size > 0) {
    let current: string | null = null;
    let currentDistance = Number.POSITIVE_INFINITY;
    let currentSignature = "\uffff";

    for (const candidate of queue) {
      const distance =
        distances.get(candidate) ?? Number.POSITIVE_INFINITY;
      const signature = signatures.get(candidate) ?? "\uffff";
      if (
        distance < currentDistance ||
        (
          Math.abs(distance - currentDistance) < 1e-12 &&
          signature < currentSignature
        )
      ) {
        current = candidate;
        currentDistance = distance;
        currentSignature = signature;
      }
    }

    if (current === null || !Number.isFinite(currentDistance)) break;
    queue.delete(current);
    if (current === target) break;

    for (const neighbor of adjacency.get(current) ?? []) {
      if (!queue.has(neighbor.nodeId)) continue;
      const nextDistance = currentDistance + neighbor.cost;
      const nextSignature = `${currentSignature}>${neighbor.nodeId}`;
      const knownDistance =
        distances.get(neighbor.nodeId) ?? Number.POSITIVE_INFINITY;
      const knownSignature =
        signatures.get(neighbor.nodeId) ?? "\uffff";

      if (
        nextDistance < knownDistance ||
        (
          Math.abs(nextDistance - knownDistance) < 1e-12 &&
          nextSignature < knownSignature
        )
      ) {
        distances.set(neighbor.nodeId, nextDistance);
        signatures.set(neighbor.nodeId, nextSignature);
        previous.set(neighbor.nodeId, {
          nodeId: current,
          edgeId: neighbor.edgeId,
        });
      }
    }
  }

  if (!previous.has(target)) return null;

  const nodeIds = [target];
  const edgeIds: string[] = [];
  let cursor = target;
  while (cursor !== source) {
    const step = previous.get(cursor);
    if (!step) return null;
    edgeIds.push(step.edgeId);
    cursor = step.nodeId;
    nodeIds.push(cursor);
  }
  nodeIds.reverse();
  edgeIds.reverse();

  return {
    nodeIds,
    edgeIds,
    totalCost:
      distances.get(target) ?? Number.POSITIVE_INFINITY,
  };
}
